import { useEffect, useRef } from "react";
import { gsap } from "gsap";

interface InteractiveImageCanvasProps {
  src: string;
  boxSize?: number;
  dotColor?: string;
  showDots?: boolean;
  fade?: boolean;
  className?: string;
}

const T = Math.PI * 2;

export default function InteractiveImageCanvas({
  src,
  boxSize = 123,
  dotColor = "#ffffff",
  showDots = true,
  fade = false,
  className,
}: InteractiveImageCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.fillStyle = dotColor;

    // Dynamic canvas dimensions matching the container's aspect ratio.
    let CW = container.clientWidth;
    let CH = container.clientHeight;
    canvas.width = CW;
    canvas.height = CH;

    let cRect = canvas.getBoundingClientRect();
    let sx = CW / cRect.width;
    let sy = CH / cRect.height;

    const m = {
      x: CW / 2,
      y: CH / 2,
      s: 1.5,
      x2: CW / 2,
      y2: CH / 2,
    };

    const xTo = gsap.quickTo(m, "x", { duration: 1, ease: "expo" });
    const yTo = gsap.quickTo(m, "y", { duration: 1, ease: "expo" });
    const sTo = gsap.quickTo(m, "s", { duration: 2, ease: "power2" });

    type Box = { x: number; y: number; d: number; s: number };
    let boxes: Box[] = [];

    // Computed cover-fit rectangle of the source image inside the canvas.
    let coverRect = { x: 0, y: 0, w: CW, h: CH };

    const computeCoverRect = () => {
      if (!img.naturalWidth || !img.naturalHeight) {
        coverRect = { x: 0, y: 0, w: CW, h: CH };
        return;
      }
      const imgRatio = img.naturalWidth / img.naturalHeight;
      const canvasRatio = CW / CH;
      let w: number;
      let h: number;
      if (imgRatio > canvasRatio) {
        // Image is wider than canvas → fit height, crop sides
        h = CH;
        w = h * imgRatio;
      } else {
        // Image is taller than canvas → fit width, crop top/bottom
        w = CW;
        h = w / imgRatio;
      }
      const x = (CW - w) / 2;
      const y = (CH - h) / 2;
      coverRect = { x, y, w, h };
    };

    const buildGrid = () => {
      boxes = [];
      for (let x = 0; x <= CW; x += boxSize) {
        for (let y = 0; y <= CH; y += boxSize) {
          boxes.push({ x, y, d: 0, s: 0 });
        }
      }
    };

    // Maps a destination rectangle on the canvas back to the source image
    // coordinates, respecting the cover-fit transform. This way every tile
    // samples the correct chunk of the original image — no black bars.
    const sampleFromImage = (
      dx: number,
      dy: number,
      dw: number,
      dh: number
    ) => {
      const scaleX = img.naturalWidth / coverRect.w;
      const scaleY = img.naturalHeight / coverRect.h;
      const srcX = (dx - coverRect.x) * scaleX;
      const srcY = (dy - coverRect.y) * scaleY;
      const srcW = dw * scaleX;
      const srcH = dh * scaleY;
      // Clamp to image bounds so drawImage never throws.
      const cx = Math.max(0, Math.min(srcX, img.naturalWidth));
      const cy = Math.max(0, Math.min(srcY, img.naturalHeight));
      const cw = Math.max(0, Math.min(srcW, img.naturalWidth - cx));
      const ch = Math.max(0, Math.min(srcH, img.naturalHeight - cy));
      return { cx, cy, cw, ch };
    };

    const drawTile = (c: Box) => {
      c.d = Math.hypot(c.x - m.x, c.y - m.y);
      c.s = 1 - gsap.utils.clamp(0, 1, c.d / CW / m.s);
      if (c.s < 0.001) return;
      const boxScaled = boxSize * c.s;
      const dx = c.x + boxScaled / 2;
      const dy = c.y + boxScaled / 2;
      const dw = boxSize - boxScaled;
      const dh = boxSize - boxScaled;
      if (dw <= 0 || dh <= 0) return;
      const { cx, cy, cw, ch } = sampleFromImage(dx, dy, dw, dh);
      if (cw <= 0 || ch <= 0) return;
      if (fade) ctx.globalAlpha = c.s;
      ctx.drawImage(img, cx, cy, cw, ch, c.x, c.y, boxSize, boxSize);
    };

    const drawDots = (c: Box) => {
      if (c.s < 0.001) return;
      ctx.beginPath();
      ctx.arc(c.x, c.y, boxSize * 0.15 * c.s, 0, T);
      ctx.fill();
    };

    let isReady = false;

    const update = () => {
      if (!isReady) return;
      const d = Math.hypot(m.x - m.x2, m.y - m.y2);
      sTo((d / CW) * 2);
      ctx.clearRect(0, 0, CW, CH);
      boxes.forEach(drawTile);
      if (fade) ctx.globalAlpha = 1;
      if (showDots) boxes.forEach(drawDots);
    };

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = src;

    img.onload = () => {
      computeCoverRect();
      buildGrid();
      isReady = true;
      gsap.ticker.add(update);
    };

    const handlePointerMove = (e: PointerEvent) => {
      m.x2 = (e.clientX - cRect.left) * sx;
      m.y2 = (e.clientY - cRect.top) * sy;
      xTo(m.x2);
      yTo(m.y2);
    };

    const handleResize = () => {
      CW = container.clientWidth;
      CH = container.clientHeight;
      canvas.width = CW;
      canvas.height = CH;
      ctx.fillStyle = dotColor;
      cRect = canvas.getBoundingClientRect();
      sx = CW / cRect.width;
      sy = CH / cRect.height;
      computeCoverRect();
      buildGrid();
    };

    canvas.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("resize", handleResize);

    return () => {
      gsap.ticker.remove(update);
      canvas.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("resize", handleResize);
    };
  }, [src, boxSize, dotColor, showDots, fade]);

  return (
    <div ref={containerRef} className={className}>
      {/* Static background image layer — full-cover, fills the whole area */}
      <img
        src={src}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover object-center select-none pointer-events-none"
        draggable={false}
      />
      {/* Interactive grid distortion layer */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full block"
      />
    </div>
  );
}